import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Receipt,
  Print,
  FileDownload,
  Refresh,
  CheckCircle,
  AccessTime,
  Close,
  TrendingUp,
  CalendarToday,
  People,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell } from '../../components/Common/DataTable';
import billingService, { Invoice, InvoiceDetail, InvoiceStats } from '../../services/billing.service';
import BillingPeriods from '../Settings/BillingPeriods';
import ClientBilling from './ClientBilling';
import { useTranslation } from 'react-i18next';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const Billing: React.FC = () => {
  const { t } = useTranslation();

  const MONTHS = [
    t('billing.january'), t('billing.february'), t('billing.march'), t('billing.april'),
    t('billing.may'), t('billing.june'), t('billing.july'), t('billing.august'),
    t('billing.september'), t('billing.october'), t('billing.november'), t('billing.december')
  ];

  // Main tab state
  const [mainTab, setMainTab] = useState(0);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [stats, setStats] = useState<InvoiceStats>({
    total: 0,
    pagas: 0,
    pendentes: 0,
    valorTotal: 0,
  });

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetail | null>(null);
  const [viewTab, setViewTab] = useState(0);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingService.getInvoices({
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        year: yearFilter ? Number(yearFilter) : undefined,
        month: monthFilter ? Number(monthFilter) : undefined,
      });

      if (response.success) {
        setInvoices(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        if (response.stats) {
          setStats(response.stats);
        }
      }
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(t('billing.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, yearFilter, monthFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleView = async (invoice: Invoice) => {
    setViewDialogOpen(true);
    setViewTab(0);
    setLoadingDetails(true);

    try {
      const response = await billingService.getInvoice(invoice.id);
      if (response.success) {
        setSelectedInvoice(response.data);
      } else {
        setSelectedInvoice({
          ...invoice,
          referencia: '',
          cliente: '',
          clienteId: '',
          servico: '',
          items: [],
          pagamentos: [],
        });
      }
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setSelectedInvoice({
        ...invoice,
        referencia: '',
        cliente: '',
        clienteId: '',
        servico: '',
        items: [
          { id: '1', descricao: 'Serviços de pagamento', quantidade: 1, valorUnitario: invoice.montante, valorTotal: invoice.montante },
        ],
        pagamentos: [],
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  const columns: Column<Invoice>[] = [
    {
      id: 'numero',
      label: t('billing.invoiceNumber'),
      minWidth: 100,
      format: (value) => (
        <Typography variant="body2" fontWeight={500} color="primary">
          {value}
        </Typography>
      ),
    },
    {
      id: 'entidade',
      label: t('common.entity'),
      minWidth: 150,
    },
    {
      id: 'mes',
      label: t('billing.month'),
      minWidth: 100,
    },
    {
      id: 'ano',
      label: t('billing.year'),
      minWidth: 70,
      align: 'center',
    },
    {
      id: 'montante',
      label: t('common.amount'),
      minWidth: 110,
      align: 'right',
      format: (value) => <CurrencyCell value={value} />,
    },
    {
      id: 'total',
      label: t('common.total'),
      minWidth: 110,
      align: 'right',
      format: (value) => <CurrencyCell value={value} />,
    },
    {
      id: 'emitidoEm',
      label: t('billing.issuedAt'),
      minWidth: 150,
      format: (value) => {
        const date = new Date(value);
        return (
          <Typography variant="body2">
            {date.toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })} {date.toLocaleTimeString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </Typography>
        );
      },
    },
    {
      id: 'estado',
      label: t('common.status'),
      minWidth: 100,
      align: 'center',
      format: (value) => (
        <StatusChip
          status={value === 'PAGA' ? t('billing.statusPaid') : value === 'PENDENTE' ? t('billing.statusPending') : value}
          colorMap={{
            [t('billing.statusPaid')]: 'success',
            PAGA: 'success',
            [t('billing.statusPending')]: 'warning',
            PENDENTE: 'warning',
            [t('billing.statusCancelled')]: 'error',
            CANCELADA: 'error',
          }}
        />
      ),
    },
  ];

  // Generate year options (last 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('billing.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('billing.subtitle')}
          </Typography>
        </Box>
        {mainTab === 0 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>
              {t('common.print')}
            </Button>
            <Button variant="outlined" startIcon={<FileDownload />}>
              {t('common.save')}
            </Button>
            <Button variant="contained" startIcon={<Refresh />} onClick={fetchInvoices}>
              {t('common.refresh')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Receipt />} iconPosition="start" label={t('billing.invoices')} />
          <Tab icon={<CalendarToday />} iconPosition="start" label={t('billing.billingPeriods')} />
          <Tab icon={<People />} iconPosition="start" label={t('billing.clients')} />
        </Tabs>
      </Paper>

      {/* Tab Content: Faturas */}
      <TabPanel value={mainTab} index={0}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                    <Receipt />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('billing.totalInvoices')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                    <CheckCircle />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.pagas}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('billing.paid')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.light', color: 'warning.main' }}>
                    <AccessTime />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats.pendentes}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('billing.pending')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                    <TrendingUp />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {formatCurrency(stats.valorTotal)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">{t('billing.totalValue')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={t('common.filter')}
                placeholder={t('billing.filterPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('billing.year')}
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('billing.month')}
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {MONTHS.map((month, index) => (
                  <MenuItem key={index} value={index + 1}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="PAGA">{t('billing.paid')}</MenuItem>
                <MenuItem value="PENDENTE">{t('billing.pending')}</MenuItem>
                <MenuItem value="CANCELADA">{t('billing.cancelled')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={invoices}
          loading={loading}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => {
            setRowsPerPage(rpp);
            setPage(0);
          }}
          onRefresh={fetchInvoices}
          onView={handleView}
          onDelete={(invoice) => console.log('Delete invoice:', invoice)}
          rowKey="id"
          emptyMessage={t('billing.noInvoices')}
        />
      </TabPanel>

      {/* Tab Content: Billing Periods */}
      <TabPanel value={mainTab} index={1}>
        <BillingPeriods />
      </TabPanel>

      {/* Tab Content: Clientes */}
      <TabPanel value={mainTab} index={2}>
        <ClientBilling />
      </TabPanel>

      {/* View Invoice Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">{t('billing.invoice')} #{selectedInvoice?.numero}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedInvoice?.entidade}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseView}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)}>
            <Tab label={t('common.details')} />
            <Tab label={t('billing.items')} />
            <Tab label={t('sidebar.payments')} />
          </Tabs>

          {loadingDetails ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('common.loading')}</Typography>
            </Box>
          ) : (
            <>
              <TabPanel value={viewTab} index={0}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('billing.invoiceNumber')}</Typography>
                    <Typography variant="body1" fontWeight={500}>{selectedInvoice?.numero}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('common.entity')}</Typography>
                    <Typography variant="body1">{selectedInvoice?.entidade}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('billing.period')}</Typography>
                    <Typography variant="body1">{selectedInvoice?.mes} {selectedInvoice?.ano}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('billing.issueDate')}</Typography>
                    <Typography variant="body1">
                      {selectedInvoice?.emitidoEm ? new Date(selectedInvoice.emitidoEm).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('common.amount')}</Typography>
                    <Typography variant="body1">{formatCurrency(selectedInvoice?.montante || 0)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('common.total')}</Typography>
                    <Typography variant="body1" fontWeight={600} color="primary">
                      {formatCurrency(selectedInvoice?.total || 0)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedInvoice?.estado === 'PAGA' ? t('billing.statusPaid') : selectedInvoice?.estado === 'PENDENTE' ? t('billing.statusPending') : selectedInvoice?.estado}
                        color={selectedInvoice?.estado === 'PAGA' ? 'success' : selectedInvoice?.estado === 'PENDENTE' ? 'warning' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={viewTab} index={1}>
                {selectedInvoice?.items && selectedInvoice.items.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.description')}</TableCell>
                        <TableCell align="center">{t('billing.qty')}</TableCell>
                        <TableCell align="right">{t('billing.unitValue')}</TableCell>
                        <TableCell align="right">{t('common.total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice?.items?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.descricao}</TableCell>
                          <TableCell align="center">{item.quantidade}</TableCell>
                          <TableCell align="right">{formatCurrency(item.valorUnitario || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.valorTotal || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t('billing.noItems')}
                  </Typography>
                )}
              </TabPanel>

              <TabPanel value={viewTab} index={2}>
                {selectedInvoice?.pagamentos && selectedInvoice.pagamentos.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.reference')}</TableCell>
                        <TableCell align="right">{t('common.value')}</TableCell>
                        <TableCell align="center">{t('common.status')}</TableCell>
                        <TableCell>{t('common.date')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice?.pagamentos?.map((pag) => (
                        <TableRow key={pag.id}>
                          <TableCell>{pag.referencia}</TableCell>
                          <TableCell align="right">{formatCurrency(pag.valor || 0)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={pag.estado}
                              color={pag.estado === 'PAGO' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {pag.dataPagamento ? new Date(pag.dataPagamento).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t('billing.noPayments')}
                  </Typography>
                )}
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseView}>{t('common.close')}</Button>
          <Button variant="outlined" startIcon={<Print />}>
            {t('common.print')}
          </Button>
          <Button variant="outlined" startIcon={<FileDownload />}>
            {t('billing.exportPdf')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
